import { useRef, useState, useCallback } from "react";
import { Modal, Alert, Button, Spin } from "antd";
import { Html5Qrcode } from "html5-qrcode";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Např. https://qr.spotlight-events.io/4415f448-fc06-4674-8017-0eb891c75fad nebo samotné UUID */
export function extractQrIdFromScan(text: string): string | null {
  const trimmed = text.trim();
  try {
    const u = new URL(trimmed);
    const parts = u.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && UUID_RE.test(last)) return last;
  } catch {
    /* not a full URL */
  }
  if (UUID_RE.test(trimmed)) return trimmed;
  return null;
}

interface QRScannerProps {
  visible: boolean;
  onClose: () => void;
  onQrIdScan: (qrId: string) => void;
}

export default function QRScanner({ visible, onClose, onQrIdScan }: QRScannerProps) {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const scanner = scannerRef.current;
        scannerRef.current = null;
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setIsScanning(false);
    setIsStarting(false);
  }, []);

  const handleScanResult = useCallback(
    (decodedText: string) => {
      const qrId = extractQrIdFromScan(decodedText);
      if (!qrId) {
        setError("Neplatný QR. Očekává se URL s UUID nebo samotné UUID.");
        return;
      }
      void stopScanner();
      onQrIdScan(qrId);
    },
    [onQrIdScan, stopScanner]
  );

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;

    const element = document.getElementById("qr-reader");
    if (!element) {
      console.error("QR reader element not found");
      return;
    }

    setError(null);
    setIsStarting(true);

    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          handleScanResult(decodedText);
        },
        () => {}
      );
      setIsScanning(true);
    } catch (err) {
      console.error("QR Scanner error:", err);
      setError("Nepodařilo se spustit kameru. Zkontrolujte oprávnění.");
      scannerRef.current = null;
    } finally {
      setIsStarting(false);
    }
  }, [handleScanResult]);

  const handleAfterOpenChange = (open: boolean) => {
    if (open) {
      setTimeout(() => {
        startScanner();
      }, 300);
    }
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <Modal
      title="Naskenujte QR kód"
      open={visible}
      onCancel={handleClose}
      afterOpenChange={handleAfterOpenChange}
      footer={
        <Button onClick={handleClose}>Zavřít</Button>
      }
      destroyOnClose
      width={350}
      centered
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ width: "100%" }}
          />
        )}

        <div
          id="qr-reader"
          style={{
            width: "100%",
            maxWidth: 300,
            minHeight: 300,
            background: "#000",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isStarting && !isScanning && (
            <Spin size="large" />
          )}
        </div>

        {isScanning && (
          <p style={{ margin: 0, color: "#666", textAlign: "center" }}>
            Namiřte kameru na QR kód (odkaz nebo UUID)
          </p>
        )}

        {isStarting && !isScanning && (
          <p style={{ margin: 0, color: "#666", textAlign: "center" }}>
            Spouštění kamery...
          </p>
        )}
      </div>
    </Modal>
  );
}
